/**
 * The generation report's vocabulary: what a local solve counts, and what the
 * summary panel reads.
 *
 * Both halves live here rather than in `pool.js` because they are one contract.
 * The panel is shared with the server path, so these key names are the Python
 * solver's — a local run and a server run have to be describable in one
 * vocabulary, or the panel would need to know which engine produced its data.
 * Keeping the counters and the snapshot that reads them in the same file is
 * what makes a rename impossible to do by halves.
 *
 * The kernel reports in integers — item ids, set ids — because that is all the
 * packed dataset holds. The panel shows names. `enrichLocalReport` closes that
 * gap in one round trip for the whole report rather than per section, because
 * the same item appears in several of them.
 *
 * Every step degrades rather than fails: a name that cannot be resolved falls
 * back to `#id`, and a request that cannot be made leaves the report intact
 * with ids in place of names. Nothing in the report is worth losing a finished
 * generation over.
 */

/** Mutation kinds the kernel counts, in the order the report charts them. */
export const MUTATION_KINDS = ['slot', 'completion', 'trim', 'swap', 'set', 'rune'];

/** Per-kind counter maps the kernel returns inside a chunk's `stats`. */
export const MUTATION_COUNTERS = [
    'mutation_attempts',
    'mutation_accepted',
    'mutation_invalid',
    'mutation_new_best',
];

/**
 * Running totals for one solve.
 *
 * The kernel counts per chunk and per island; the panel wants one number per
 * mutation kind and per rejection reason for the whole run, so chunks fold in
 * here as they land rather than being kept and summed at the end.
 */
export function newStats() {
    const counters = {};
    for (const name of MUTATION_COUNTERS) {
        counters[name] = Object.fromEntries(MUTATION_KINDS.map((k) => [k, 0]));
    }
    return { ...counters, invalid_reasons: {}, proposals: 0, accepted: 0, new_best_updates: 0 };
}

/** Fold one chunk's `stats` into the running totals. Returns `into`. */
export function mergeStats(into, chunk) {
    if (!chunk) return into;
    for (const name of MUTATION_COUNTERS) {
        const src = chunk[name] || {};
        for (const kind of MUTATION_KINDS) {
            into[name][kind] += Number(src[kind]) || 0;
        }
    }
    for (const [reason, n] of Object.entries(chunk.invalid_reasons || {})) {
        into.invalid_reasons[reason] = (into.invalid_reasons[reason] || 0) + (Number(n) || 0);
    }
    into.proposals += Number(chunk.proposals) || 0;
    into.accepted += Number(chunk.accepted) || 0;
    into.new_best_updates += Number(chunk.new_best_updates) || 0;
    return into;
}

/**
 * When the run reached the build it ends up delivering.
 *
 * Lives here rather than inline in `pool.js` only because that file imports a
 * worker through `import.meta.url` and so cannot be loaded by the test runner
 * at all — and this is the bookkeeping most worth pinning down, because every
 * mistake in it is a plausible-looking number rather than a crash.
 */
export function newProgressTrack() {
    return { series: [], firstImprovementMs: null, bestAtMs: null, seedScore: null };
}

/**
 * The seed build, with points allocated, as the curve's starting point.
 *
 * The kernel's own `seed_soft` is the search objective — no characteristic
 * points invested — while every other point on the chart is scored after
 * allocating all of them. Charting one against the other made "+178 vs seed"
 * mostly the worth of the points, and stretched the y-axis so far that every
 * real gain looked flat. Not an improvement, so neither timing moves.
 */
export function recordSeed(track, score) {
    track.seedScore = score;
    track.series.push({ elapsed_s: 0, best_score: score });
    return track;
}

/** A finalized incumbent beat the previous one, mid-run. */
export function recordImprovement(track, atMs, score) {
    if (track.firstImprovementMs === null) track.firstImprovementMs = atMs;
    track.bestAtMs = atMs;
    track.series.push({ elapsed_s: atMs / 1000, best_score: score });
    return track;
}

/**
 * The post-deadline finalize round produced something better than anything seen
 * during the run.
 *
 * That round runs on every island over the elites each banked across the whole
 * search, so it beats the mid-run best by a hair on most runs — but it is a
 * rescore of gear the search already found, not a discovery at the buzzer.
 * Stamping it with the elapsed time made "time → best" read as the full budget
 * almost every run, while the convergence chart beside it plainly showed the
 * score settling in the first second.
 *
 * So the score is amended onto the last step rather than added as a new one:
 * the chart ends on the delivered score, at the time that build was found. The
 * exception is a run that finalized nothing until now, where this really is
 * when the only build arrived.
 */
export function settleDelivered(track, atMs, score) {
    if (track.bestAtMs === null) return recordImprovement(track, atMs, score);
    track.series[track.series.length - 1].best_score = score;
    return track;
}

/**
 * The metrics snapshot the generation report reads.
 *
 * `cache_hit_rate` is deliberately absent rather than zero. The Rust kernel has
 * no evaluation cache at all, and reporting 0% would read as a cache performing
 * terribly instead of one that does not exist; the panel drops the card when
 * the number is missing.
 */
export function solveMetrics({ stats, evals, elapsedMs, totalMs, bestFinal, track }) {
    const { bestAtMs, firstImprovementMs } = track;
    const invalid = Object.values(stats.invalid_reasons).reduce((a, b) => a + b, 0);
    return {
        elapsed_s: elapsedMs / 1000,
        time_limit_s: totalMs / 1000,
        eval_count: evals,
        invalid_count: invalid,
        invalid_ratio_pct: stats.proposals > 0 ? (invalid / stats.proposals) * 100 : 0,
        acceptance_rate: stats.proposals > 0 ? stats.accepted / stats.proposals : null,
        seed_score: track.seedScore ?? bestFinal?.seed_soft ?? null,
        best_score: bestFinal?.soft ?? null,
        new_best_updates: stats.new_best_updates,
        first_improvement_s: firstImprovementMs == null ? null : firstImprovementMs / 1000,
        best_at_s: bestAtMs == null ? null : bestAtMs / 1000,
        mutation_attempts: stats.mutation_attempts,
        mutation_accepted: stats.mutation_accepted,
        mutation_invalid: stats.mutation_invalid,
        mutation_new_best: stats.mutation_new_best,
        invalid_reasons: stats.invalid_reasons,
    };
}

/** Placeholder for an id the server did not know, in the panel's name shape. */
function idFallback(id) {
    return { fr: `#${id}`, en: `#${id}` };
}

/** Every id the report mentions, so names come back in one request. */
function collectIds(report) {
    const items = new Set();
    const sets = new Set();
    for (const list of Object.values(report.candidates || {})) {
        for (const c of list || []) items.add(c.id);
    }
    for (const entry of report.setCandidates || []) {
        if (entry.set_id) sets.add(entry.set_id);
        for (const id of entry.item_ids || []) items.add(id);
    }
    for (const build of report.elitePool || []) {
        for (const id of build.item_ids || []) if (id > 0) items.add(id);
    }
    return { items: [...items], sets: [...sets] };
}

/**
 * @param {object} report the `report` a local solve resolved with
 * @param {(itemIds: number[], setIds: number[]) => Promise<{items: object, sets: object}>} resolveNames
 * @returns {Promise<object|null>} the same report with names filled in
 */
export async function enrichLocalReport(report, resolveNames) {
    if (!report) return null;
    const { items: itemIds, sets: setIds } = collectIds(report);
    let names = { items: {}, sets: {} };
    if (itemIds.length > 0 || setIds.length > 0) {
        try {
            names = (await resolveNames(itemIds, setIds)) || names;
        } catch (_err) {
            // Ids it is.
        }
    }
    const itemName = (id) => names.items?.[String(id)] || idFallback(id);
    const setInfo = (id) => names.sets?.[String(id)] || { name: idFallback(id), level: 0 };

    const candidates = {};
    for (const [slot, list] of Object.entries(report.candidates || {})) {
        candidates[slot] = (list || []).map((c) => ({ ...c, name: itemName(c.id) }));
    }

    const setCandidates = (report.setCandidates || []).map((entry) => {
        const info = setInfo(entry.set_id);
        return {
            ...entry,
            name: info.name,
            level: info.level,
            // Named `items` because that is the key the panel's tooltip reads;
            // the ids stay on `item_ids`.
            items: (entry.item_ids || []).map((id) => {
                const n = itemName(id);
                return typeof n === 'object' ? (n.fr || n.en || `#${id}`) : n;
            }),
        };
    });

    const elitePool = (report.elitePool || []).map((build) => ({
        ...build,
        item_names: (build.item_ids || [])
            .filter((id) => id > 0)
            .map((id) => itemName(id)),
    }));

    return { ...report, candidates, setCandidates, elitePool };
}

export default enrichLocalReport;
